import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { OceanRadar, TraitBars } from "../components/Charts";
import { Download, HeartHandshake, ArrowRight, Sparkles, Leaf, Compass, Loader2 } from "lucide-react";

function Card({ children, className = "" }) {
  return <div className={`bg-white border border-[#E8E1D9] rounded-2xl p-6 sm:p-8 shadow-sm ${className}`}>{children}</div>;
}
function BulletList({ items, color }) {
  return (
    <ul className="space-y-2.5">
      {items?.map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-[#2B2625]">
          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
          <span className="text-[15px] leading-relaxed">{t}</span>
        </li>
      ))}
    </ul>
  );
}
function Skeleton({ lines = 3 }) {
  return (
    <div className="space-y-2.5 animate-pulse" data-testid="narrative-skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-3.5 rounded-full bg-[#EFE7DD]" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export default function PersonalReport() {
  const { id } = useParams();
  const location = useLocation();
  const pairId = location.state?.pairId;
  const [data, setData] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await api.report(id);
        if (!alive) return;
        setData(d);
        if (d.status !== "ready") timer.current = setTimeout(load, 3000);
      } catch { /* ignore */ }
    };
    load();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [id]);

  if (!data) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[#918780]">Loading your report…</div>;
  const { profile, narrative, user_name, status } = data;
  const ready = status === "ready" && narrative;

  const oceanItems = Object.keys(profile.ocean).map((k) => ({ label: profile.ocean_names[k], value: profile.ocean[k] }));
  const discItems = Object.keys(profile.disc).map((k) => ({ label: profile.disc_names[k], value: profile.disc[k] }));

  return (
    <div className="max-w-4xl mx-auto px-5 py-10 space-y-6">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-3xl bg-[#6B21A8] text-white p-8 sm:p-11 relative overflow-hidden">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#F3E3FC]">Your personality report</span>
        <h1 className="font-display text-3xl sm:text-5xl font-bold mt-2 leading-tight" data-testid="report-headline">
          {ready ? narrative.headline : "Your profile is ready"}
        </h1>
        <p className="text-[#F3E3FC] mt-3 text-lg">{user_name} · {profile.headline_type} · Type {profile.mbti}</p>
      </motion.div>

      {!ready && (
        <div className="flex items-center gap-3 rounded-xl bg-[#FEF6E8] text-[#3B0764] px-4 py-3 text-sm font-medium" data-testid="report-generating">
          <Loader2 className="animate-spin" size={16} /> Your scores are in! We're writing your personal insights — just a few seconds…
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-4">Your core traits</h3>
          <OceanRadar ocean={profile.ocean} names={profile.ocean_names} />
        </Card>
        <Card>
          <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-5">Trait breakdown</h3>
          <TraitBars items={oceanItems} color="#6B21A8" />
        </Card>
      </div>

      <Card>
        <h3 className="font-display text-xl font-semibold text-[#2B2625] mb-5">Your behavioural styles</h3>
        <TraitBars items={discItems} color="#3F3A44" />
      </Card>

      <Card>
        <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-2"><Sparkles size={17} /> Who you are</div>
        {ready ? <p className="text-[#2B2625] text-lg leading-relaxed">{narrative.overview}</p> : <Skeleton lines={3} />}
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-4"><Sparkles size={17} /> Natural strengths</div>
          {ready ? <BulletList items={narrative.strengths} color="#6B21A8" /> : <Skeleton lines={4} />}
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-[#7C3AED] font-semibold mb-4"><Leaf size={17} /> Gentle growth areas</div>
          {ready ? <BulletList items={narrative.growth_areas} color="#7C3AED" /> : <Skeleton lines={3} />}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center gap-2 text-[#3F3A44] font-semibold mb-3"><HeartHandshake size={17} /> How you connect</div>
          {ready ? <p className="text-[#2B2625] leading-relaxed">{narrative.relationships}</p> : <Skeleton lines={2} />}
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-[#3F3A44] font-semibold mb-3"><Compass size={17} /> How you work</div>
          {ready ? <p className="text-[#2B2625] leading-relaxed">{narrative.work_style}</p> : <Skeleton lines={2} />}
        </Card>
      </div>

      {ready && (
        <Card className="bg-[#F2E9FA] border-[#E4D6F5]">
          <p className="font-display text-xl sm:text-2xl text-[#3F3A44] italic text-center leading-relaxed">
            "{narrative.affirmation}"
          </p>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        {ready ? (
          <a href={api.reportPdf(id)} target="_blank" rel="noreferrer" data-testid="report-download-pdf"
             className="inline-flex items-center gap-2 bg-[#F2E9FA] hover:bg-[#E4D6F5] text-[#3F3A44] font-semibold py-3 px-6 rounded-xl transition-colors">
            <Download size={18} /> Download PDF
          </a>
        ) : (
          <span className="inline-flex items-center gap-2 bg-[#F3EDE4] text-[#918780] font-semibold py-3 px-6 rounded-xl">
            <Loader2 className="animate-spin" size={16} /> Preparing PDF…
          </span>
        )}
        <Link to="/dashboard" data-testid="report-dashboard" className="text-[#6E655F] hover:text-[#6B21A8] font-medium px-2">
          Go to dashboard
        </Link>
      </div>

      {pairId ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-[#3F3A44] text-white p-8 sm:p-10 text-center">
          <h3 className="font-display text-2xl sm:text-3xl font-bold mb-2">Now for the fun part…</h3>
          <p className="text-[#E4D6F5] mb-6 max-w-lg mx-auto">Your shared report is ready. See exactly how the two of you connect, complement, and click.</p>
          <Link to={`/combination/${pairId}`} data-testid="report-see-combination"
            className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#5B21B6] text-[#2B2625] font-semibold py-3.5 px-8 rounded-xl transition-colors active:scale-[0.98]">
            See how you connect <ArrowRight size={18} />
          </Link>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-[#3F3A44] text-white p-8 sm:p-10 text-center relative overflow-hidden">
          <h3 className="font-display text-2xl sm:text-3xl font-bold mb-2">Who do you connect with most?</h3>
          <p className="text-[#E4D6F5] mb-1 max-w-xl mx-auto">
            Here's where it gets interesting. Invite one person you're curious about — a friend, your partner,
            a colleague — and discover how your two personalities fit together.
          </p>
          <p className="text-[#7C3AED] font-semibold mb-6">Your first invite is on us.</p>
          <Link to="/invite" data-testid="report-invite-cta"
            className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#5B21B6] text-[#2B2625] font-semibold py-3.5 px-8 rounded-xl transition-colors active:scale-[0.98]">
            Invite someone <ArrowRight size={18} />
          </Link>
        </motion.div>
      )}
    </div>
  );
}
